import {Header} from "@/components/layouts/main/Header";
import {Chat} from "@/pages/common/ChatGame/Chat";
import {Controls} from "@/pages/common/ChatGame/Controls";
import {Timer} from "@/pages/common/ChatGame/Timer";
import {
  clearChatState,
  createCertificateSession,
  fetchPatientBriefing,
  getQuestion,
  setServerStatus,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useReducer, useRef} from "react";

import {useChatTime} from "@hooks/ChatTime";
import {useHeygenAvatar} from "@hooks/HeygenAvatar";
import {LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE} from "@hooks/HeygenAvatar/utils";
import doctorImage from "@images/general/doctor.png";
import patientImage from "@images/general/patient.webp";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {AiFeedbackPopup} from "@popups/simulationPopups/AiFeedbackPopup";
import {LevelPopup} from "@popups/simulationPopups/LevelPopup";
import {MicrophonePermissionPopup} from "@popups/simulationPopups/MicrophonePermissionPopup";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {ChromaKeyVideoPlayer} from "@ui/general/ChromaKeyVideoPlayer";
import {Spinner} from "@ui/general/Spinner";
import {RootIcon} from "@ui/icons/RootIcon";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate, useParams} from "react-router-dom";

import styles from "../styles.module.scss";

/**
 * Компонент для тестовых серверов
 * Не используется в продакшене
 * Есть возможность выбора - начать игру с аватаром или без
 * Есть возможность перезапустить игру с изначальным выбором
 */

export const ChatHeygenGame = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {openPopup, closePopup} = usePopup();
  const sessionType = params?.session;
  const sessionLevel = params?.level;
  const sessionId = params?.id;
  // Course ("feedback" | "copilot") for AVA Tutor scenario-less sessions;
  // undefined for real scenario/certification sessions.
  const sessionCourse = location?.state?.level?.course;

  const remainingTime = location?.state?.level?.remainingTime;

  const isMedical = window.location.host.includes("ava-medical");

  const isTutor =
    window.location.host.includes("aicoach") ||
    window.location.host.includes("avatutor") ||
    window.location.host.includes("localhost");

  const [chatTime, setChatTimeState, restart] = useChatTime(remainingTime);

  const CHAT_LIFECYCLE = {
    CHOOSING: "choosing",
    STARTING: "starting",
    ACTIVE: "active",
    FEEDBACK: "feedback",
  };

  const [lifecycle, lifecycleDispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case "START":
          return {status: CHAT_LIFECYCLE.STARTING, mode: action.mode};
        case "ACTIVE":
          return {...state, status: CHAT_LIFECYCLE.ACTIVE};
        case "FEEDBACK":
          return {...state, status: CHAT_LIFECYCLE.FEEDBACK};
        case "RESET":
          return {
            status: CHAT_LIFECYCLE.CHOOSING,
            mode: action.mode ?? state.mode ?? null,
          };
        default:
          return state;
      }
    },
    {
      status: CHAT_LIFECYCLE.CHOOSING,
      mode: null,
    },
  );

  const isSessionInitialized = lifecycle.status !== CHAT_LIFECYCLE.CHOOSING;
  const hasAvatar = lifecycle.mode === "avatar" && isSessionInitialized;
  const isFeedbackPopupOpen = lifecycle.status === CHAT_LIFECYCLE.FEEDBACK;
  const isInitialProductQuestionRequestedRef = useRef(false);

  const {
    volume,
    messages,
    product_session_id,
    scenario_session_id,
    certificate_session_id,
    score,
    questions,
    sessionState,
    error,
    serverStatus,
  } = useSelector((state) => state.ml.chat);

  const {isMobile} = useSelector((state) => state.common.settings);

  const {
    avatarSessionState,
    isAvatarTalking,
    mediaStream,
    AvatarSessionState,
    ensureMicrophonePermission,
    initAvatarWithToken,
    stopAvatar,
    stopVoice,
    showAvatarError,
    clearAllAvatarState,
  } = useHeygenAvatar(
    openPopup,
    isSessionInitialized,
    hasAvatar,
    volume,
    remainingTime,
  );

  const initSession = useCallback(async () => {
    try {
      isInitialProductQuestionRequestedRef.current = false;

      let result;
      switch (sessionType) {
        case "scenario":
          result = await dispatch(
            createCertificateSession({
              level: sessionLevel,
              scenario_id: sessionId,
              course: sessionCourse,
            }),
          );
          break;

        case "product":
          result = await dispatch(
            createCertificateSession({
              level: sessionLevel,
              scenario_id: sessionId,
              course: sessionCourse,
            }),
          );
          break;

        case "certification":
          result = await dispatch(
            createCertificateSession({
              level: sessionLevel,
              scenario_id: sessionId,
              course: sessionCourse,
            }),
          );
          break;

        default:
          throw new Error("Invalid session type");
      }

      const createdSessionId = result?.payload?.id;
      // Log the dispatch decision itself, not just its failures: a skipped
      // dispatch and a failed request look identical on screen. This is what
      // caught the briefing thunk being rejected by a bad `condition`. Kept
      // deliberately — see the note above buildBriefingMessages in chatSlice.
      console.info("[AVA:briefing] init", {
        sessionType,
        course: sessionCourse,
        createdSessionId,
        dispatching: Boolean(createdSessionId) && sessionType !== "product",
      });

      if (createdSessionId && sessionType !== "product") {
        const briefingAction = await dispatch(
          fetchPatientBriefing({
            id: createdSessionId,
            sessionType,
            course: sessionCourse,
          }),
        );

        // `condition` in the thunk drops re-entrant dispatches; when it does,
        // the action is aborted and nothing else in the thunk ever runs.
        console.info("[AVA:briefing] dispatch result", {
          type: briefingAction?.type,
          aborted: briefingAction?.meta?.condition === true,
        });
      }

      setChatTimeState(remainingTime);
    } catch (error) {
      console.log("Session initialization error: ", error);
      dispatch(setServerStatus("error"));
    } finally {
      dispatch(setServerStatus(""));
    }
  }, [
    dispatch,
    remainingTime,
    sessionId,
    sessionLevel,
    sessionType,
    sessionCourse,
    setChatTimeState,
  ]);

  const isMicrophonePermissionError = (error) =>
    error?.code === LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE ||
    error?.cause?.code === LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE ||
    error?.name === "NotAllowedError" ||
    error?.cause?.name === "NotAllowedError";

  const startSessionFlow = useCallback(
    async (mode) => {
      closePopup();
      lifecycleDispatch({type: "START", mode});

      try {
        if (mode === "avatar") {
          await ensureMicrophonePermission();
        }
        await initSession();
        if (mode === "avatar") {
          await initAvatarWithToken();
        }
        lifecycleDispatch({type: "ACTIVE"});
        return {ok: true};
      } catch (error) {
        if (mode === "avatar" && isMicrophonePermissionError(error)) {
          lifecycleDispatch({type: "RESET", mode});
          return {ok: false, reason: "microphone_denied"};
        }
        lifecycleDispatch({type: "RESET", mode});
        return {ok: false, reason: "error", error};
      }
    },
    [closePopup, ensureMicrophonePermission, initAvatarWithToken, initSession],
  );

  const requestMicrophonePermissionForPopup = useCallback(async () => {
    try {
      await ensureMicrophonePermission();
      return true;
    } catch (_error) {
      return false;
    }
  }, [ensureMicrophonePermission]);

  const openMicrophonePermissionPopup = useCallback(() => {
    openPopup(
      <MicrophonePermissionPopup
        requestMicrophoneAccess={requestMicrophonePermissionForPopup}
        continueWithAvatar={async () => {
          const result = await startSessionFlow("avatar");
          if (result?.reason === "error") {
            showAvatarError(handleTryAgain, handleQuit, result.error);
          }
        }}
        continueWithoutAvatar={() => startSessionFlow("text")}
      />,
      {closeButton: false},
    );
  }, [
    openPopup,
    requestMicrophonePermissionForPopup,
    startSessionFlow,
    showAvatarError,
  ]);

  const handleClickQuitBtn = async () => {
    try {
      if (product_session_id) {
        if (messages.length > 2) {
          // Оставляем ACTIVE, чтобы сработал эффект показа feedback popup.
          restart(Date.now(), false);
        } else {
          await dispatch(clearChatState());
          lifecycleDispatch({type: "RESET"});
          navigate(-1);
        }
      } else if (scenario_session_id || certificate_session_id) {
        if (messages.length > 1) {
          // Оставляем ACTIVE, чтобы сработал эффект показа feedback popup.
          restart(Date.now(), false);
        } else {
          await dispatch(clearChatState());
          lifecycleDispatch({type: "RESET"});
          navigate(-1);
        }
      } else {
        await dispatch(clearChatState());
        lifecycleDispatch({type: "RESET"});
        navigate(-1);
      }
    } catch (error) {
      console.error("Error handling quit button:", error);
    }
  };

  const handleQuit = useCallback(async () => {
    try {
      await dispatch(clearChatState());
      lifecycleDispatch({type: "RESET"});
      closePopup();
      navigate(-1);
    } catch (error) {
      console.log("Exit error: ", error);
      dispatch(setServerStatus("error"));
    } finally {
      dispatch(setServerStatus(""));
    }
  }, [dispatch, closePopup, navigate]);

  const handleTryAgain = useCallback(async () => {
    try {
      closePopup();
      await clearAllAvatarState();
      lifecycleDispatch({type: "RESET"});
      const mode = lifecycle.mode || "text";
      const result = await startSessionFlow(mode);
      if (result?.reason === "microphone_denied") {
        openMicrophonePermissionPopup();
        return;
      }
      if (result?.reason === "error") {
        showAvatarError(handleTryAgain, handleQuit, result.error);
        return;
      }
      restart(new Date(Date.now() + remainingTime), true);
    } catch (error) {
      console.log("Retry error: ", error);
      dispatch(setServerStatus("error"));
    } finally {
      dispatch(setServerStatus(""));
    }
  }, [
    closePopup,
    clearAllAvatarState,
    lifecycle.mode,
    remainingTime,
    restart,
    startSessionFlow,
    openMicrophonePermissionPopup,
    showAvatarError,
  ]);

  useEffect(() => {
    openPopup(
      <LevelPopup
        close={() => startSessionFlow("text")}
        closeWithAvatar={async () => {
          const result = await startSessionFlow("avatar");
          if (result?.reason === "microphone_denied") {
            openMicrophonePermissionPopup();
            return;
          }
          if (result?.reason === "error") {
            showAvatarError(handleTryAgain, handleQuit, result.error);
          }
        }}
        item={location?.state?.level}
      />,
      {
        onClose: () => navigate(-1),
      },
    );

    return () => {
      closePopup();
    };
  }, []);

  useEffect(() => {
    if (lifecycle.status !== CHAT_LIFECYCLE.ACTIVE) {
      return;
    }

    const userMessages = messages.filter((m) => m?.sender === "user").length;
    const botMessages = messages.filter((m) => m?.sender === "bot").length - 1;

    let shouldShow = false;

    switch (true) {
      case scenario_session_id && score === 0:
      case (scenario_session_id || certificate_session_id) &&
        sessionState === "ended":
      case (scenario_session_id || certificate_session_id) && chatTime === 0:
      case product_session_id && chatTime === 0:
      case product_session_id &&
        questions === 0 &&
        userMessages === botMessages:
        shouldShow = true;
        break;
      default:
        shouldShow = false;
    }

    if (shouldShow) {
      if (avatarSessionState === AvatarSessionState.CONNECTED && !stopVoice)
        return;

      if (!isFeedbackPopupOpen) {
        if (avatarSessionState === AvatarSessionState.CONNECTED) {
          stopAvatar().then(() => {
            openPopup(
              <AiFeedbackPopup
                again={handleTryAgain}
                quit={handleQuit}
                withFeedback={userMessages !== 0}
              />,
              {
                closeButton: false,
              },
            );
            lifecycleDispatch({type: "FEEDBACK"});
          });
        } else {
          openPopup(
            <AiFeedbackPopup
              again={handleTryAgain}
              quit={handleQuit}
              withFeedback={userMessages !== 0}
            />,
            {
              closeButton: false,
            },
          );
          lifecycleDispatch({type: "FEEDBACK"});
        }
      }
    }
  }, [
    lifecycle.status,
    chatTime,
    questions,
    score,
    sessionState,
    scenario_session_id,
    product_session_id,
    certificate_session_id,
    messages,
    avatarSessionState,
    stopVoice,
    stopAvatar,
    openPopup,
    handleTryAgain,
    handleQuit,
    isFeedbackPopupOpen,
    AvatarSessionState,
  ]);

  useEffect(() => {
    if (
      !product_session_id ||
      messages.length !== 1 ||
      isInitialProductQuestionRequestedRef.current
    ) {
      return;
    }

    const requestInitialQuestion = () => {
      if (isInitialProductQuestionRequestedRef.current) return;
      isInitialProductQuestionRequestedRef.current = true;
      dispatch(getQuestion({id: product_session_id}));
    };

    if (hasAvatar) {
      if (avatarSessionState !== AvatarSessionState.CONNECTED || !stopVoice)
        return;
      requestInitialQuestion();
      return;
    }

    requestInitialQuestion();
  }, [
    avatarSessionState,
    hasAvatar,
    product_session_id,
    stopVoice,
    messages.length,
    dispatch,
  ]);

  return (
    <div className={styles.gameWrapper}>
      <Header />

      <div className={"container-big flex h-[100%] phone:p-0"}>
        <div className={styles.game}>
          <div
            className={`${styles.doctor} ${isMedical || isTutor ? styles.doctorMedical : styles.doctorTrainer}`}
          >
            {(product_session_id ||
              scenario_session_id ||
              certificate_session_id) &&
            avatarSessionState !== AvatarSessionState.INACTIVE &&
            error === "" ? (
              <>
                {avatarSessionState === AvatarSessionState.CONNECTING && (
                  <>
                    <Spinner
                      width={80}
                      height={80}
                    />

                    <img
                      className={styles.doctorImage}
                      src={isMedical || isTutor ? patientImage : doctorImage}
                      alt={isMedical || isTutor ? "patient" : "doctor"}
                    />
                  </>
                )}

                {avatarSessionState === AvatarSessionState.CONNECTED && (
                  <ChromaKeyVideoPlayer
                    videoRef={mediaStream}
                    isConnected={
                      avatarSessionState === AvatarSessionState.CONNECTED
                    }
                    videoId={"avatarVideo"}
                    videoProps={{
                      autoPlay: true,
                      playsInline: true,
                      muted: false,
                    }}
                    wrapperClassName={`${styles.avatarWrapper} ${isMedical || isTutor ? styles.avatarWrapperMedical : styles.avatarWrapperTrainer}`}
                    playerClassName={`${styles.avatarPlayer} ${isMedical || isTutor ? styles.avatarPlayerMedical : styles.avatarPlayerTrainer}`}
                  />
                )}

                {avatarSessionState === AvatarSessionState.CONNECTED && (
                  <>
                    <Controls />

                    <div
                      onClick={handleClickQuitBtn}
                      className={`${styles.quitBtn} ${isMedical || isTutor ? styles.quitBtnMedical : styles.quitBtnTrainer}`}
                      style={
                        serverStatus === "loading" || isAvatarTalking
                          ? {
                              opacity: 0.5,
                              pointerEvents: "none",
                            }
                          : {
                              opacity: 1,
                              pointerEvents: "auto",
                            }
                      }
                    >
                      <i>
                        <RootIcon
                          width={32}
                          height={32}
                          id={"quit"}
                        />
                      </i>

                      {!isMobile && (
                        <RootDesc>
                          <span>{t("button.exit")}</span>
                        </RootDesc>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <img
                  className={styles.doctorImage}
                  src={isMedical || isTutor ? patientImage : doctorImage}
                  alt={isMedical || isTutor ? "patient" : "doctor"}
                />

                <div
                  onClick={() => handleClickQuitBtn(restart)}
                  className={`${styles.quitBtn} ${isMedical || isTutor ? styles.quitBtnMedical : styles.quitBtnTrainer}`}
                  style={
                    serverStatus === "loading" || isAvatarTalking
                      ? {
                          opacity: 0.5,
                          pointerEvents: "none",
                        }
                      : {
                          opacity: 1,
                          pointerEvents: "auto",
                        }
                  }
                >
                  <i>
                    <RootIcon
                      width={32}
                      height={32}
                      id={"quit"}
                    />
                  </i>

                  {!isMobile && (
                    <RootDesc>
                      <span>{t("button.exit")}</span>
                    </RootDesc>
                  )}
                </div>
              </>
            )}
          </div>

          <Timer time={chatTime} />

          <div className={styles.gameChatWrapper}>
            <Chat
              time={chatTime}
              hasAvatar={hasAvatar}
              isAvatarTalking={isAvatarTalking}
              avatarSessionState={avatarSessionState}
              AvatarSessionState={AvatarSessionState}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
